



























































































































































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import { getBuildDate, getRunningBuild } from "@/utils/BuildInfo";
import { clearCacheAndModules, clearUserModule } from "@/utils/Utils";
import { clearViewAsSelection } from "@/utils/ViewAsSession";
import { auth } from "firebase/app";

import router from "@/router/router";
import { loadClient } from "@/data/repository/ClientRepository";
import SharedEmailAddressOptionsDialogComponent from "@/components/login/SharedEmailAddressOptionsDialog.vue";
import {
  getActiveUser,
  requestResetPassword,
  setActiveUser,
  signInWithUserEmail,
  signInWithUserOfSharedEmailAddress,
} from "@/data/repository/UserRepository";
import { iresSettingsAccess } from "@/permissions/UserPermissions";
import { isClientSignInBlocked, signInRefusalMessageKey, toSignInRefusalReason } from "@/utils/SignInRefusal";
import {
  CLIENT_HANDOVER_QUERY_KEY,
  clientLoginRefusalKey,
  isHandoverNonce,
  receiveClientLoginToken,
} from "@/utils/ClientLoginHandover";
import { IRES_ADMIN_ACCOUNT } from "@/data/models/User";
import { loadExternalSystem } from "@/data/repository/ExternalSystemRepository";
import { getSharedEmailAddressInfo } from "@/data/repository/SharedEmailAddressRepository";
import { ClientModule } from "@/store/modules/ClientModule";
import { resolveOfficeAdminPortalClient } from "@/data/repository/AdministrationRepository";
import { LoginResult, LoginTarget, loginResultForRefusal } from "./LoginTypes";
import {
  COLOR_LOGIN_BUTTON_DARK,
  COLOR_STANDARD_BLACK,
  COLOR_STANDARD_WHITE,
} from "@/utils/colors";

@Component({
  components: {
    "shared-email-address-options": SharedEmailAddressOptionsDialogComponent,
  },
})
export default class Login extends Vue {
  @Ref("sharedEmailAddressOptions")
  sharedEmailAddressOptions!: SharedEmailAddressOptionsDialogComponent;

  isResetPasswordDialog = false;

  email = "";
  password = "";

  showPassword = false;
  loginError: string | null = null;
  resetPasswordEmailSentSuccess: string | null = null;
  signingIn = false;
  /** A tab opened by "Als Mandant anmelden" (#2610069): shows progress instead of the form. */
  isClientHandover = false;

  sharedUserPassword: string | null = null;

  loginTarget = LoginTarget.CLIENT;

  showSessionEndedHint = false;

  @Watch("email")
  onEmailChanged() {
    this.loginError = null;
    this.resetPasswordEmailSentSuccess = null;
  }

  @Watch("password")
  onPasswordChanged() {
    this.loginError = null;
    this.resetPasswordEmailSentSuccess = null;
  }

  get isDevelopmentWebsite() {
    return window.location.hostname.includes("development");
  }

  get noInternetErrorMessage() {
    return this.$t("login.noInternetAvailable").toString();
  }

  get networkBlockedErrorMessage() {
    return this.$t("login.networkBlocked").toString();
  }

  get shouldDisableLoginButton() {
    if (!this.loginError) {
      return false;
    }

    const connectivityMessages = [
      this.noInternetErrorMessage,
      this.networkBlockedErrorMessage,
    ];

    return !connectivityMessages.includes(this.loginError);
  }

  get versionString() {
    const dev = this.isDevelopmentWebsite;

    return this.$t(
      dev
        ? "common.development"
        : "common.production",
      [getRunningBuild().version]
    ).toString();
  }

  get showDevDetails() {
    return !window.location.hostname.includes("localhost");
  }

  get devDetails() {
    const build = getRunningBuild();

    return (
      this.versionString +
      " / " +
      this.$t("common.build", [
        String(build.buildNumber),
        getBuildDate(build),
      ]).toString()
    );
  }

  get isIresLogin() {
    return !this.isBelforLogin && !this.isSvtLogin
  }

  get isBelforLogin() {
    return window.location.href.toLowerCase().includes("belfor");
  }

  get isSvtLogin() {
    return window.location.href.toLowerCase().includes("svt");
  }

  get showPasswortInput() {
    return !this.isBelforLogin && !this.isSvtLogin;
  }

  /** Focus colour of the login fields (caret and underline), readable on either background. */
  get textFieldColor() {
    return this.$vuetify.theme.dark ? "white" : "black";
  }

  /**
   * Text colour of a field the browser autofilled. Chrome forces autofilled text to black, which
   * vanishes on the dark background once its tinted field background is suppressed (see styles).
   */
  get autofillTextColor() {
    return this.$vuetify.theme.dark ? COLOR_STANDARD_WHITE : COLOR_STANDARD_BLACK;
  }

  /** Fill of the login button: black on the light background, a soft off-white on the dark one. */
  get loginButtonColor() {
    return this.$vuetify.theme.dark ? COLOR_LOGIN_BUTTON_DARK : "black";
  }

  /** Label of the login button, the opposite of its fill so it stays readable in both themes. */
  get loginButtonLabelClass() {
    return this.$vuetify.theme.dark ? "black--text" : "white--text";
  }

  get emailAddressInputHint() {
    return this.$t(
      `login.${this.isBelforLogin || this.isSvtLogin ? "emailAddress" : "userIdentification"}`
    ).toString();
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  async created() {
    // #2610069: a tab opened by "Als Mandant anmelden" in the IRES Admin Client. Set before any await,
    // so the first render is the progress view, never the form.
    const handoverNonce = router.currentRoute.query[CLIENT_HANDOVER_QUERY_KEY];
    if (isHandoverNonce(handoverNonce)) {
      this.isClientHandover = true;
      await this.signInFromClientHandover(handoverNonce);

      return;
    }

    const shouldShowResetPasswordDialog = router.currentRoute.query["resetPassword"] === "true";
    if (shouldShowResetPasswordDialog) {
      await this.toggleForgotPassword();
    }
    else {
      this.showSessionEndedHint = router.currentRoute.query["sessionEnd"] === "1";

      // #2610066: refused by the single sign-on, or a running session signed out because blocked.
      const refusedReason = toSignInRefusalReason(router.currentRoute.query["refusedReason"]);
      if (refusedReason) {
        this.loginError = this.$t(signInRefusalMessageKey(refusedReason)).toString();

        return;
      }

      const unknownEmailAddress = router.currentRoute.query[
        "unknownEmailAddress"
      ] as string;

      if (unknownEmailAddress) {
        this.loginError = this.$t("login.unknownEmailAddress", [
          this.isSvtLogin ? "SVT" : "BELFOR",
          unknownEmailAddress,
        ]).toString();

        return;
      }

      const token = router.currentRoute.query["token"] as string;
      const userId = router.currentRoute.query["userId"] as string;
      const sharedEmailAddressId = router.currentRoute.query[
        "sharedEmailAddressId"
      ] as string;

      if (token && (sharedEmailAddressId || userId)) {
        await auth().setPersistence(auth.Auth.Persistence.SESSION);
        await auth().signInWithCustomToken(token);

        let result = LoginResult.FAILURE;

        if (userId) {
          result = await setActiveUser(userId);
        } else if (sharedEmailAddressId) {
          result = await this.onLoginWithSharedEmailAddress(sharedEmailAddressId);
        }

        if (result == LoginResult.SUCCESS) {
          await this.onSuccessfulLogin();
        } else if (result === LoginResult.CLIENT_BLOCKED || result === LoginResult.USER_BLOCKED) {
          this.loginError = this.$t(
            signInRefusalMessageKey(result === LoginResult.USER_BLOCKED ? "USER_BLOCKED" : "CLIENT_BLOCKED")
          ).toString();
        }
      }
    }
  }

  /**
   * "Als Mandant anmelden" (#2610069): takes the token from the IRES Admin Client tab that opened this one
   * (never from the URL, which carries only the nonce) and signs in as the client's IRES admin user,
   * as the token sign-in above does. Without a token this tab stays signed out and says why: IRES
   * Cloud's refusal when the admin tab passed one on (#2610071), else the general failure.
   */
  async signInFromClientHandover(nonce: string) {
    this.signingIn = true;
    const { token, refusal } = await receiveClientLoginToken(nonce);
    if (!token) {
      this.loginError = refusal
        ? this.$t("login.clientHandover.refused", [this.$t(clientLoginRefusalKey(refusal)).toString()]).toString()
        : this.$t("login.clientHandover.failed").toString();
      this.signingIn = false;

      return;
    }

    try {
      await auth().setPersistence(auth.Auth.Persistence.SESSION);
      await auth().signInWithCustomToken(token);
      const result = await setActiveUser(IRES_ADMIN_ACCOUNT);
      if (result === LoginResult.SUCCESS) {
        await this.onSuccessfulLogin();
      } else {
        this.loginError = this.$t("login.clientHandover.failed").toString();
      }
    } catch {
      // An expired or refused token: the tab stays signed out with the message.
      this.loginError = this.$t("login.clientHandover.failed").toString();
    } finally {
      this.signingIn = false;
    }
  }

  async onLoginWithSharedEmailAddress(sharedEmailAddressId: string) {
    this.signingIn = true;
    const sharedEmailAddressInfo = await getSharedEmailAddressInfo(
      sharedEmailAddressId
    );

    if (!sharedEmailAddressInfo) {
      return LoginResult.FAILURE;
    }

    //Automatically log into the base client
    const result = await signInWithUserOfSharedEmailAddress(
      sharedEmailAddressInfo.id,
      sharedEmailAddressInfo.sourceUserId,
      sharedEmailAddressInfo.sourceClientId,
      false,
    );

    if (result.wasSuccessful && result.token) {
      await auth().signInWithCustomToken(result.token);

      const setUserResult = await setActiveUser(
        sharedEmailAddressInfo.sourceUserId,
        this.sharedUserPassword
      );

      this.signingIn = false;

      return setUserResult;
    }

    // #2610066: signed in to the address in the first step; its user is refused, so leave again.
    const refusal = toSignInRefusalReason(result.reason);
    if (refusal) {
      await auth().signOut();
      this.signingIn = false;

      return loginResultForRefusal(refusal);
    }

    return LoginResult.WRONG_PASSWORD;
  }

  async toggleForgotPassword() {
    this.password = "";
    this.isResetPasswordDialog = !this.isResetPasswordDialog;
  }

  async onSuccessfulLogin() {
    clearCacheAndModules(false, true);
    // A login always starts in the own view, even the same user's in the same tab (ticket 2610050).
    clearViewAsSelection();

    const client = await loadClient();

    if (!client) {
      const externalSystem = await loadExternalSystem();

      if (!externalSystem) {
        this.loginError = this.$t("login.loginFailed").toString();
      } else {
        router.replace({ path: "/main/projects" });
      }
    } else {
      // Resolve admin-portal membership before the portal renders, so the navigation and the
      // settings page get the right shape on the very first frame. Cached in ClientModule and read
      // synchronously via isOfficeAdminPortalClient(). Never fatal: a failed lookup just means the
      // client-facing shape is shown, and the portal's real gate is the route guard.
      await resolveOfficeAdminPortalClient();

      // #2610066: the client account's own email login goes to Firebase directly, never through IRES
      // Cloud's refusal. The same IRES exemption as the running-session check in Scaffold.
      if (isClientSignInBlocked(client) && !iresSettingsAccess(getActiveUser())) {
        await auth().signOut();
        this.loginError = this.$t(signInRefusalMessageKey("CLIENT_BLOCKED")).toString();

        return;
      }

      router.replace({ path: "/main/cockpit" });
    }
  }

  async submitLoginData() {
    this.loginError = null;

    if (this.signingIn) {
      return;
    }

    if (!navigator.onLine) {
      this.loginError = this.noInternetErrorMessage;
      return;
    }

    if (this.isResetPasswordDialog) {
      // IRES Cloud refuses a request without an address, so ask for one instead of sending it.
      if (!this.email.trim()) {
        this.loginError = this.$t("login.pleaseSignInWithEmail").toString();
        return;
      }

      this.signingIn = true;
      // TODO: Check if email has been sent in last 5 minutes.

      try {
        const { wasSuccessful, isSharedUserEmailAddress } = await requestResetPassword(this.email, this.isDevelopmentWebsite);

        if (wasSuccessful) {
          this.resetPasswordEmailSentSuccess = this.$t("login.passwordResetEmailSent").toString();
        }
        else {
          this.loginError = isSharedUserEmailAddress
            ? this.$t("login.passwordResetSharedEmailError").toString()
            : this.$t("login.passwordResetEmailError").toString();
        }
      } catch {
        // A failed request must not leave signingIn set: the button would spin and ignore every later submit.
        this.loginError = this.$t("login.passwordResetEmailError").toString();
      } finally {
        this.signingIn = false;
      }

      return;
    }

    ClientModule.clear();
    clearUserModule();

    this.signingIn = true;

    const emailAddress = this.email.toLowerCase();

    if (this.isBelforLogin) {
      this.signingIn = false;
      //Redirect to sso for belfor
      window.open(
        "https://europe-west1-ires-cloud.cloudfunctions.net/belfor/v1/sso/login"
      );

      return;
    }
    else if (this.isSvtLogin) {
      this.signingIn = false;
      //Redirect to sso for svt
      window.open(
        "https://europe-west1-ires-cloud.cloudfunctions.net/svt/v1/sso/login"
      );

      return;
    }

    const password = this.password;
    this.loginTarget = LoginTarget.CLIENT;

    let loginResult = await this.signInWithClientEmailAddress(
      emailAddress,
      password
    );

    if (loginResult === LoginResult.NO_MATCHING_CLIENT_OR_USER) {
      const { result, sharedEmailAddressId, sharedUserPassword } = await this.signInWithUserEmail(
        emailAddress,
        password
      );

      if (result === LoginResult.SHARED_EMAIL_ADDRESS && sharedEmailAddressId) {
        this.signingIn = false;

        if (sharedUserPassword) {
          this.sharedUserPassword = sharedUserPassword;
        }

        loginResult = await this.onLoginWithSharedEmailAddress(
          sharedEmailAddressId
        );
      } else {
        loginResult = result;
      }

      if (loginResult == LoginResult.WRONG_PASSWORD) {
        this.loginTarget = LoginTarget.USER;
      }
    }

    if (loginResult === LoginResult.SUCCESS) {
      await this.onSuccessfulLogin();
    } else {
      if (loginResult === LoginResult.NO_INTERNET_CONNECTION) {
        this.loginError = this.noInternetErrorMessage;
      }
      else if (loginResult === LoginResult.NETWORK_BLOCKED) {
        this.loginError = this.networkBlockedErrorMessage;
      }
      else if (
        loginResult === LoginResult.NO_MATCHING_CLIENT_OR_USER ||
        loginResult === LoginResult.WRONG_PASSWORD
      ) {
        this.loginError = this.$t("login.loginFailed").toString();
      } else if (loginResult === LoginResult.USER_BLOCKED) {
        this.loginError = this.$t("login.loginFailedBlocked", [
          emailAddress,
        ]).toString();
      } else if (loginResult === LoginResult.CLIENT_BLOCKED) {
        this.loginError = this.$t(signInRefusalMessageKey("CLIENT_BLOCKED")).toString();
      }
    }

    this.signingIn = false;
  }

  async signInWithClientEmailAddress(emailAddress: string, password: string) {
    try {
      await auth().setPersistence(auth.Auth.Persistence.SESSION);

      const result = await auth().signInWithEmailAndPassword(
        emailAddress,
        password
      );

      const user = result.user;

      if (user) {
        return LoginResult.SUCCESS;
      } else {
        return LoginResult.NO_MATCHING_CLIENT_OR_USER;
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).code === "auth/wrong-password") {
        return LoginResult.WRONG_PASSWORD;
      } else {
        return LoginResult.NO_MATCHING_CLIENT_OR_USER;
      }
    }
  }

  async signInWithUserEmail(emailAddress: string, password: string) {
    try {
      return await signInWithUserEmail(emailAddress, password);
    } catch (error) {
      if (!navigator.onLine) {
        return { result: LoginResult.NO_INTERNET_CONNECTION };
      }
      return {
        result: LoginResult.NO_MATCHING_CLIENT_OR_USER,
      };
    }
  }
}
